import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Upload, FileText, Download, Loader2 } from 'lucide-react';
import { SharedFile } from '../types';
import { api } from '../lib/api';

interface FileShareModalProps {
  publicId: string;
  onClose: () => void;
}

export const FileShareModal: React.FC<FileShareModalProps> = ({ publicId, onClose }) => {
  const [files, setFiles] = useState<SharedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const loadFiles = useCallback(async () => {
    try {
      const data = await api.getFiles(publicId);
      setFiles(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load files');
    }
  }, [publicId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    try {
      await api.uploadFile(publicId, file);
      await loadFiles();
    } catch (err: any) {
      setError(err.message || 'File upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="w-full h-full bg-[#121622] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-slate-800/80 flex items-center justify-between">
        <h3 className="font-semibold text-white text-sm">Meeting Files (R2)</h3>
        <button
          onClick={onClose}
          className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Upload Zone */}
      <div className="p-4 border-b border-slate-800/60 bg-[#0c0f17]/40">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full py-3 px-4 border border-dashed border-slate-700 hover:border-blue-500 rounded-xl flex items-center justify-center space-x-2 text-xs font-semibold text-slate-300 hover:text-white bg-slate-900/40 hover:bg-slate-800/50 transition disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
              <span>Uploading to R2...</span>
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 text-blue-400" />
              <span>Upload File to Meeting</span>
            </>
          )}
        </button>

        {error && <p className="mt-2 text-[11px] text-rose-400 font-medium">{error}</p>}
      </div>

      {/* File List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {files.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center text-slate-500 text-xs">
            <p>No files shared in this meeting yet.</p>
          </div>
        ) : (
          files.map((file) => {
            const downloadUrl = api.getFileDownloadUrl(publicId, file.id);
            return (
              <div
                key={file.id}
                className="p-3 rounded-xl bg-slate-900/60 border border-slate-800/80 flex items-center justify-between hover:border-slate-700 transition"
              >
                <div className="flex items-center space-x-3 min-w-0 pr-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 flex-shrink-0">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-200 truncate">{file.name}</p>
                    <p className="text-[10px] text-slate-400">
                      {formatSize(file.size)} • by {file.uploaderName}
                    </p>
                  </div>
                </div>

                <a
                  href={downloadUrl}
                  download={file.name}
                  target="_blank"
                  rel="noreferrer"
                  title="Download File"
                  className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition flex-shrink-0"
                >
                  <Download className="w-4 h-4" />
                </a>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
